import {
  OPPORTUNITIES_AGGREGATE_FIELDS_ENUM,
  OPPORTUNITIES_AGGREGATE_COMPARE_TO_RESULT_KEYS_ENUM,
  OPPORTUNITIES_AGGREGATE_COMPARE_TO_TITLES_ENUM,
  OPPORTUNITIES_STACK_BY_TYPE_ENUM,
  VALUATIONS_AGGREGATE_BY_TITLE,
} from '../../../constants';

import { titleize } from '../../../../../../../utils/helpers';
import { useGetMaOpportunitiesAggregates } from '../../../hooks/useGetMaOpportunitiesAggregates';
import { useGetMaOpportunitiesAggregatesStacked } from '../../../hooks/useGetMaTransactionsOpportunitiesStacked';
import { useGetMaOpportunitiesForChart } from '../../../hooks/useGetMaOpportunitiesForChart';
import { findOpportunitiesForGroup, createHoverText } from '../../../helpers/chartHelpers';
import { useMemo } from 'react';

const MAX_LEGEND_ITEMS = 5;

const getAltYAxisChartData = (data, aggregateBy, compareTo) => {
  const aggregateByTitle = titleize(aggregateBy);
  const altYAxisTitle = OPPORTUNITIES_AGGREGATE_COMPARE_TO_TITLES_ENUM[compareTo];

  return [
    {
      x: data?.map((d) => d.group) || [],
      y: data?.map((d) => d[OPPORTUNITIES_AGGREGATE_COMPARE_TO_RESULT_KEYS_ENUM[compareTo]]) || [],
      name: altYAxisTitle,
      yaxis: 'y2',
      type: 'scatter',
      mode: 'markers',
      hovertemplate: `${aggregateByTitle}: %{x}<br>${altYAxisTitle}: %{y:,.2f}<extra></extra>`,
    },
  ];
};

const getFlatChartData = (data, aggregateBy, compareTo, opportunities) => {
  const aggregateByTitle = titleize(aggregateBy);
  const yAxisTitle = VALUATIONS_AGGREGATE_BY_TITLE;

  return [
    {
      x: data?.map((d) => d.group) || [],
      y: data?.map((d) => d.valuationsSum) || [],
      name: yAxisTitle,
      type: 'bar',
      customdata: data?.map((d) => {
        const groupOpportunities = findOpportunitiesForGroup(opportunities, d.group, null, null);
        if (groupOpportunities && groupOpportunities.length > 0) {
          return createHoverText(
            groupOpportunities,
            aggregateByTitle,
          );
        }
        return '';
      }) || [],
      hovertemplate: `
        <b>${aggregateByTitle}:</b> %{x}<br>
        <b>${yAxisTitle}:</b> %{y:,.2f}<br>

        <b>Opportunities:</b><br>
        %{customdata}
        <extra></extra>
      `.trim(),
    },
    ...getAltYAxisChartData(data, aggregateBy, compareTo),
  ];
};

const getStackedChartData = (data, stackedData, stacks, aggregateBy, stackBy, compareTo, opportunities) => {
  const yAxisTitle = VALUATIONS_AGGREGATE_BY_TITLE;
  const aggregateByTitle = titleize(aggregateBy);
  const stackByTitle = titleize(stackBy);

  const chartX = [];
  const chartYs = new Map(stacks.map((stack) => [stack, []]));
  const chartCustomData = new Map(stacks.map((stack) => [stack, []]));
  
  // fill in the X and and primary Y values for each stack
  stackedData.forEach((d) => {
    // get list of X-axis values (e.g. countries, years, etc.)
    chartX.push(d.group);
    // get list of Y-axis values for each stack (i.e., buyer, seller)
    // if stack value is not present in the data, we need to default to 0 to keep the chart consistent
    stacks.forEach((stack) => {
      chartYs.get(stack).push(d.values[stack]?.valuationsSum || 0);
      
      // Add custom data for hover information
      const groupOpportunities = findOpportunitiesForGroup(opportunities, d.group, stackBy, stack);
      if (groupOpportunities && chartCustomData.has(stack)) {
        chartCustomData.get(stack)?.push(groupOpportunities);
      }
    });
  });

  return [...chartYs]
    .map(([stack, chartY], idx) => ({
      // same X-axis values for all stacks
      x: chartX,
      // one Y-axis value for stack
      y: chartY,
      name: stack,
      type: 'bar',
      
      customdata: chartCustomData.get(stack).map((opportunities) => {
        if (opportunities && opportunities.length > 0) {
          return createHoverText(
            opportunities,
            aggregateByTitle,
            stackByTitle,
          );
        }
        return '';
      }),
      hovertemplate: `
        <b>${stackByTitle}:</b> ${stack}
        <br><b>${aggregateByTitle}:</b> %{x}
        <br><b>${yAxisTitle}:</b> %{y:,.2f}
        %{customdata}
      `.trim(),
      showlegend: idx < MAX_LEGEND_ITEMS,
    }))
    .concat(getAltYAxisChartData(data, aggregateBy, compareTo));
};

export const useGetChartData = ({ 
  aggregateBy = 'country', 
  stackBy = 'none',
  compareTo, 
  fields,
}) => {
  const { data, loading, error } = useGetMaOpportunitiesAggregates(aggregateBy, fields);

  // only query for stacked data if we are stacking by a valid field
  const skipStackedQuery =
    stackBy === OPPORTUNITIES_STACK_BY_TYPE_ENUM.NONE ||
    !Object.values(OPPORTUNITIES_STACK_BY_TYPE_ENUM).includes(stackBy);
  // we only allow stacking values for the primary Y-axis, which for now it's always valuations
  const stackByFields = [OPPORTUNITIES_AGGREGATE_FIELDS_ENUM.VALUATIONS];
  
  const {
    data: stackedData,
    stacks,
    loading: stackedLoading,
    error: stackedError,
  } = useGetMaOpportunitiesAggregatesStacked(aggregateBy, stackBy, stackByFields, skipStackedQuery);

  // Smart conditional fetching: only fetch opportunities when needed
  const shouldSkipDetails = stackBy === OPPORTUNITIES_STACK_BY_TYPE_ENUM.NONE;
  const shouldFetchOpportunities = useMemo(() => {
    const shouldFetch = !skipStackedQuery && 
           !shouldSkipDetails && 
           stackedData?.length > 0;
    
    return shouldFetch;
  }, [skipStackedQuery, shouldSkipDetails, stackedData?.length]);

  // Fetch individual opportunities for detailed hover information
  const { opportunities: opportunitiesData, loading: opportunitiesLoading } = useGetMaOpportunitiesForChart({
    skip: !shouldFetchOpportunities,
    chartData: stackedData || [],
  });

  const chartData = useMemo(() => {
    if (!data) return [];

    const opportunities = opportunitiesData || [];

    return skipStackedQuery
      ? getFlatChartData(data, aggregateBy, compareTo, opportunities)
      : getStackedChartData(data, stackedData, stacks, aggregateBy, stackBy, compareTo, opportunities);
  }, [stackBy, data, stackedData, opportunitiesData, aggregateBy, compareTo, skipStackedQuery, stacks]);

  return { 
    data: chartData, 
    loading: loading || stackedLoading || opportunitiesLoading, 
    error: error || stackedError,
  };
};
